/**
 * Internal Staff Contracts
 *
 * DATABASE-FIRST: Schemas match PostgreSQL exactly
 * Source: MCP postgres queries on 2026-01-02
 *
 * Table: staff
 * Columns: identifier (PK), role, active, created_at, last_active_date
 * CHECK constraint valid_role: super_admin, admin, worker, referee
 *
 * Used by:
 * - admin-bot → backend (verify staff before granting bot access)
 * - withdrawal-bot → backend (verify worker status)
 */
import { z } from 'zod';
import { bigintString } from '../common.js';
// Import staff role from admin/staff.ts (already defined there)
import { staffRoleSchema } from '../admin/staff.js';
// =============================================================================
// VERIFY STAFF
// Endpoint: POST /internal/staff/verify
// Used by bots to check if a telegram ID is authorized staff
// =============================================================================
/**
 * Staff verification request
 * Body: { telegram_id: string }
 */
export const verifyStaffRequestSchema = z.object({
    telegram_id: bigintString, // Telegram user ID as string (bigint)
});
/**
 * Staff verification response - Not staff
 */
export const verifyStaffNotFoundResponseSchema = z.object({
    success: z.literal(true),
    is_staff: z.literal(false),
});
/**
 * Staff verification response - Is staff
 */
export const verifyStaffFoundResponseSchema = z.object({
    success: z.literal(true),
    is_staff: z.literal(true),
    role: staffRoleSchema,
    identifier: z.string(), // Obfuscated telegram ID
});
/**
 * Staff verification response - Discriminated union
 */
export const verifyStaffResponseSchema = z.discriminatedUnion('is_staff', [
    verifyStaffNotFoundResponseSchema,
    verifyStaffFoundResponseSchema,
]);
/**
 * Staff verification error response
 */
export const verifyStaffErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.string(),
});
// =============================================================================
// VALIDATE ADMIN PASSWORD
// Endpoint: POST /internal/staff/validate-password
// Legacy endpoint - validates against ADMIN_PASSWORD env var
// =============================================================================
/**
 * Admin password validation request
 */
export const validateAdminPasswordRequestSchema = z.object({
    password: z.string().min(1),
});
/**
 * Admin password validation response
 */
export const validateAdminPasswordResponseSchema = z.object({
    success: z.literal(true),
    valid: z.boolean(),
});
/**
 * Admin password validation error response
 */
export const validateAdminPasswordErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.string(),
});
//# sourceMappingURL=staff.js.map